import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validatorsv2.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { CheckboxField } from '@connected/form-fields/components/checkbox';
import { DateTimeField } from '@connected/form-fields/components/datetime';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleCheckOptionItem,
    SingleHorizontalOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Separator } from '@connected/ui/components/separator';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { format, parseISO } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Option } from '@/hooks/use-option';

import { ElectricalProjectFilePreview } from '@/components/electrical-project-file-preview';
import { MeasurementDeviceSelectionTable } from '@/components/measurement-device-selection-table';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { useElectricalProjectFiles } from '@/hooks/use-electrical-project-files';
import { useOnline } from '@/hooks/use-online';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/information/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

type InspectionScheduleInformationFormValues = {
    startDate: Date;
    endDate: Date;
};

type InspectionInformationFormValues = {
    reportNumber: string;
    enableExtendedRisoOnDistributionBoardDevices: boolean;
    distributionBoardMeasurementRegistrationKind: components['schemas']['ElectricalProjectDistributionBoardMeasurementRegistrationKind'] | null;
};

function Page() {
    const { t } = useTranslation('re-inspections');

    return (
        <>
            <Container>
                <Container.Header>
                    <Container.Title>{t('information.title')}</Container.Title>
                </Container.Header>
                <Container.Content>
                    <Form />
                </Container.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Container>
        </>
    );
}

function Form() {
    const { t } = useTranslation('re-inspections');

    const isOnline = useOnline();
    const params = Route.useParams();
    const { inspectionKindsArray } = useElectricalProject();

    const schedules = useQuery(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
    const schedulesData = schedules?.data?.Schedule;

    const inspections = useQuery(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    const inspectionsData = inspections?.data?.Inspection;

    const measurementDevices = useQuery(elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
    const measurementDevicesData = measurementDevices?.data?.Devices;

    const selectedMeasurementDevices = useQuery(elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));

    const measurementDevicesOptions = selectedMeasurementDevices?.data?.Devices?.map(item => ({ value: item.ElectricalMeasurementDeviceId!, label: item.Name! })) || ([] as Option<string>[]);

    const selectMeasurementDeviceCreate = api.useMutation('post', '/api/v2/electrical/projects/measurementdevices/create', {
        onMutate: async (variables) => {
            const body = variables?.body;

            const selectableMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const selectedMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousSelectableMeasurementDevicesData = queryClient.getQueryData(selectableMeasurementDevicesQueryOptions.queryKey) as components['schemas']['CreateElectricalProjectMeasurementDeviceQueryResult'];
            const previousSelectedMeasurementDevicesData = queryClient.getQueryData(selectedMeasurementDevicesQueryOptions.queryKey) as components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'];
            const newSelectableMeasurementDevicesData: components['schemas']['CreateElectricalProjectMeasurementDeviceQueryResult'] = {
                Devices: previousSelectableMeasurementDevicesData?.Devices?.filter(item => item.ElectricalMeasurementDeviceId !== body?.ElectricalMeasurementDeviceId),
            };
            const newSelectedMeasurementDevicesData: components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'] = {
                Devices: [
                    ...(previousSelectedMeasurementDevicesData?.Devices || []),
                    {
                        ...previousSelectableMeasurementDevicesData?.Devices?.find(item => item.ElectricalMeasurementDeviceId === body?.ElectricalMeasurementDeviceId),
                        ...body,
                        InspectionKinds: body?.InspectionKinds || [],
                    },
                ],
            };

            logger.debug({
                message: 'onMutate(selectableMeasurmentDevicesQueryOptions)',
                path: '/api/v2/electrical/projects/measurementdevices/create',
                selectableMeasurementDevices: {
                    previousData: previousSelectableMeasurementDevicesData,
                    newData: newSelectableMeasurementDevicesData,
                    queryOptions: selectableMeasurementDevicesQueryOptions,
                    body,
                },
            });
            logger.debug({
                message: 'onMutate(selectedMeasurementDevicesQueryOptions)',
                path: '/api/v2/electrical/projects/measurementdevices/create',
                selectedMeasurementDevices: {
                    previousData: previousSelectedMeasurementDevicesData,
                    newData: newSelectedMeasurementDevicesData,
                    queryOptions: selectedMeasurementDevicesQueryOptions,
                    body,
                },
            });

            queryClient.setQueryData(selectableMeasurementDevicesQueryOptions.queryKey, newSelectableMeasurementDevicesData);
            queryClient.setQueryData(selectedMeasurementDevicesQueryOptions.queryKey, newSelectedMeasurementDevicesData);

            return { previousSelectableMeasurementDevicesData, previousSelectedMeasurementDevicesData, selectableMeasurementDevicesQueryOptions, selectedMeasurementDevicesQueryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        onError: async (_error, _variables, context: any) => {
            const selectableMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const selectedMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            queryClient.setQueryData(selectableMeasurementDevicesQueryOptions.queryKey, context?.previousSelectableMeasurementDevicesData);
            queryClient.setQueryData(selectedMeasurementDevicesQueryOptions.queryKey, context?.previousSelectedMeasurementDevicesData);
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    const selectMeasurementDeviceRemove = api.useMutation('delete', '/api/v2/electrical/projects/measurementdevices/delete', {
        onMutate: async (variables) => {
            const body = variables?.params?.query;

            const selectableMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const selectedMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousSelectableMeasurementDevicesData = queryClient.getQueryData(selectableMeasurementDevicesQueryOptions.queryKey) as components['schemas']['CreateElectricalProjectMeasurementDeviceQueryResult'];
            const previousSelectedMeasurementDevicesData = queryClient.getQueryData(selectedMeasurementDevicesQueryOptions.queryKey) as components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'];
            const newSelectableMeasurementDevicesData: components['schemas']['CreateElectricalProjectMeasurementDeviceQueryResult'] = {
                Devices: [
                    ...(previousSelectableMeasurementDevicesData?.Devices || []),
                    {
                        ...previousSelectedMeasurementDevicesData?.Devices?.find(item => item.ElectricalProjectMeasurementDeviceId === body?.ElectricalProjectMeasurementDeviceId),
                        ElectricalMeasurementDeviceId: body.ElectricalProjectMeasurementDeviceId!,
                    },
                ],
            };
            const newSelectedMeasurementDevicesData: components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'] = {
                Devices: previousSelectedMeasurementDevicesData?.Devices?.filter(item => item.ElectricalProjectMeasurementDeviceId !== body?.ElectricalProjectMeasurementDeviceId),
            };

            logger.debug({
                message: 'onMutate(selectableMeasurementDevicesQueryOptions)',
                path: '/api/v2/electrical/projects/measurementdevices/delete',
                selectableMeasurementDevices: {
                    previousData: previousSelectableMeasurementDevicesData,
                    newData: newSelectableMeasurementDevicesData,
                    queryOptions: selectableMeasurementDevicesQueryOptions,
                    body,
                },
            });
            logger.debug({
                message: 'onMutate(selectedMeasurementDevicesQueryOptions)',
                path: '/api/v2/electrical/projects/measurementdevices/delete',
                selectedMeasurementDevices: {
                    previousData: previousSelectedMeasurementDevicesData,
                    newData: newSelectedMeasurementDevicesData,
                    queryOptions: selectedMeasurementDevicesQueryOptions,
                    body,
                },
            });

            queryClient.setQueryData(selectableMeasurementDevicesQueryOptions.queryKey, newSelectableMeasurementDevicesData);
            queryClient.setQueryData(selectedMeasurementDevicesQueryOptions.queryKey, newSelectedMeasurementDevicesData);

            return { previousSelectableMeasurementDevicesData, previousSelectedMeasurementDevicesData, selectableMeasurementDevicesQueryOptions, selectedMeasurementDevicesQueryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        onError: async (_error, _variables, context: any) => {
            const selectableMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            const selectedMeasurementDevicesQueryOptions = elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({ electricalProjectVersionId: params.projectVersionId });
            queryClient.setQueryData(selectableMeasurementDevicesQueryOptions.queryKey, context?.previousSelectableMeasurementDevicesData);
            queryClient.setQueryData(selectedMeasurementDevicesQueryOptions.queryKey, context?.previousSelectedMeasurementDevicesData);
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    const inspectionScheduleInformationForm = useForm<InspectionScheduleInformationFormValues, ZodValidator>({
        defaultValues: {
            startDate: schedulesData?.StartDate
                ? parseISO(schedulesData.StartDate)
                : new Date(),
            endDate: schedulesData?.EndDate
                ? parseISO(schedulesData.EndDate)
                : new Date(),
        },
        validatorAdapter: zodValidator(),
    });

    const inspectionInformationForm = useForm<InspectionInformationFormValues, ZodValidator>({
        defaultValues: {
            reportNumber: inspectionsData?.ReportNumber || '', // /api/v2/electrical/projects/inspections/edit
            enableExtendedRisoOnDistributionBoardDevices: inspectionsData?.EnableExtendedRisoOnDistributionBoardDevices || false,
            distributionBoardMeasurementRegistrationKind: inspectionsData?.DistributionBoardMeasurementRegistrationKind || null,
        },
        validatorAdapter: zodValidator(),
    });

    const schedulePatcher = usePatcher('/api/v2/electrical/projects/inspections/schedules/patch', {
        onMutate: async (variables) => {
            const body = variables?.body;

            const queryOptions = elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['EditElectricalProjectInspectionScheduleQueryResult'];
            const newData: components['schemas']['EditElectricalProjectInspectionScheduleQueryResult'] = {
                ...previousData,
                Schedule: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    ...previousData.Schedule,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(scheduleQueryOptions)',
                path: '/api/v2/electrical/projects/inspections/schedules/patch',
                schedule: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        scope: {
            id: params.projectVersionId,
        },
    }, { body: { ElectricalProjectVersionId: params.projectVersionId } });

    const informationPatcher = usePatcher('/api/v2/electrical/projects/inspections/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            const queryOptions = elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['EditElectricalProjectInspectionQueryResult'];
            const newData: components['schemas']['EditElectricalProjectInspectionQueryResult'] = {
                ...previousData,
                Inspection: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    ...previousData.Inspection,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(informationQueryOptions)',
                path: '/api/v2/electrical/projects/inspections/patch',
                information: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        scope: {
            id: params.projectVersionId,
        },
    }, { body: { ElectricalProjectVersionId: params.projectVersionId } });

    const inspectorUsers = useQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: params.projectVersionId, userKind: 'Inspector' }));

    const usersData = inspectorUsers?.data?.Users || [];

    const userOptions = usersData?.map(user => ({ label: user.DisplayName!, value: user.UserId! })) || [];

    const [selectedUsers, setSelectedUsers] = useState<string[]>([...(usersData?.filter(x => x.Selected === true)?.map(x => x.UserId!) || [])]);

    const selectedProjectUserMutation = api.useMutation('put', '/api/v2/electrical/projects/users/edit', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            await queryClient.cancelQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                electricalProjectVersionId: params.projectVersionId,
                userKind: 'Inspector',
            }));

            const queryOptions = elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                electricalProjectVersionId: params.projectVersionId,
                userKind: 'Inspector',
            });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectUserQueryResult'];

            const newData: components['schemas']['ListElectricalProjectUserQueryResult'] = {
                ...previousData,
                Users: previousData.Users.map((user) => {
                    if (user.UserId === body.UserId) {
                        return {
                            ...user,
                            Selected: body.Selected,
                            Kind: body.Kind,
                        };
                    }
                    return user;
                }),
            };

            await queryClient.setQueryData(queryOptions.queryKey, newData);

            logger.debug({
                message: 'onMutate(/api/v2/electrical/projects/users/edit)',
                path: '/api/v2/electrical/projects/users/edit',
                user: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            return { previousData, queryOptions };
        },
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: params.projectVersionId, userKind: 'Inspector' }));
        },
    });

    function handleUserChange(values: string[]) {
        const existingSelected = selectedUsers;
        const newSelected = values;

        const toAdd = newSelected.filter(x => !existingSelected.includes(x));
        const toRemove = existingSelected.filter(
            x => !newSelected.includes(x),
        );
        setSelectedUsers(newSelected);

        toAdd.forEach(async (item) => {
            await selectedProjectUserMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    UserId: item,
                    Selected: true,
                    Kind: usersData.find(x => x.UserId === item)?.Kind || 'Inspector',

                },
            });
        });

        toRemove.forEach(async (item) => {
            await selectedProjectUserMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    UserId: item,
                    Selected: false,
                    Kind: usersData.find(x => x.UserId === item)?.Kind || 'Inspector',
                },
            });
        });
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
                <inspectionScheduleInformationForm.Field
                    name="startDate"
                    validators={{
                        onChange: validators.patchElectricalProjectInspectionScheduleCommand.startDate,
                        onChangeAsync: schedulePatcher.patchField('StartDate', (value: unknown) => format(value as Date, 'yyyy-MM-dd\'T\'HH:mm:ss')),
                        onChangeAsyncDebounceMs: schedulePatcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state }) => (
                        <Label label={t('information.form.startDate.label')}>
                            <DateTimeField
                                value={state.value}
                                onChange={handleChange}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </inspectionScheduleInformationForm.Field>
                <inspectionScheduleInformationForm.Field
                    name="endDate"
                    validators={{
                        onChange:
                            validators.patchElectricalProjectInspectionScheduleCommand.endDate,
                        onChangeAsync: schedulePatcher.patchField(
                            'EndDate',
                            (value: unknown) =>
                                format(value as Date, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        ),
                        onChangeAsyncDebounceMs: schedulePatcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state }) => (
                        <Label label={t('information.form.endDate.label')}>
                            <DateTimeField
                                value={state.value}
                                onChange={handleChange}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </inspectionScheduleInformationForm.Field>
            </div>

            <Label
                label={t('information.form.inspectors.label')}
                className="w-full col-span-2"
            >
                <MultipleOptionField
                    handleChange={(values) => {
                        handleUserChange(values);
                    }}
                    state={{ value: selectedUsers }}
                    options={userOptions}
                    render={MultipleOptionItem}
                    emptyOptionsMessage={t('information.form.inspectors.emptyMessage')}
                    placeholder={t('information.form.inspectors.placeholder')}
                    hideSelected
                    dropdown
                />
            </Label>

            <inspectionInformationForm.Field
                name="reportNumber"
                validators={{
                    onChange:
                        validators.patchElectricalProjectInspectionCommand.reportNumber,
                    onChangeAsync: informationPatcher.patchField('ReportNumber'),
                    onChangeAsyncDebounceMs: informationPatcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.documentNumber.label')}>
                        <Input
                            name={name}
                            value={state.value}
                            onChange={e => handleChange(e.target.value)}
                            placeholder={t(
                                'information.form.documentNumber.placeholder',
                            )}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </inspectionInformationForm.Field>

            <inspectionInformationForm.Field
                name="enableExtendedRisoOnDistributionBoardDevices"
                validators={{
                    onChange: validators.patchElectricalProjectInspectionCommand.enableExtendedRisoOnDistributionBoardDevices,
                    onChangeAsync: informationPatcher.patchField('EnableExtendedRisoOnDistributionBoardDevices'),
                    onChangeAsyncDebounceMs: informationPatcher.patchField.debounceTime,
                }}
                children={field => (
                    <CheckboxField
                        value={field.state.value}
                        onChange={field.handleChange}
                        label={t('information.form.enableExtendedRiso.label')}
                    />
                )}
            />

            <inspectionInformationForm.Field
                name="distributionBoardMeasurementRegistrationKind"
                validators={{
                    onChange:
                        validators.patchElectricalProjectInspectionCommand.distributionBoardMeasurementRegistrationKind,
                    onChangeAsync: informationPatcher.patchField('DistributionBoardMeasurementRegistrationKind'),
                    onChangeAsyncDebounceMs: informationPatcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.measurementRegistrationKind.label')} htmlFor="distributionBoardMeasurementRegistrationKind">
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            className="w-full flex flex-col md:flex-row"
                            state={state}
                            options={[
                                {
                                    label: t('information.form.measurementRegistrationKind.options.input'),
                                    value: 'Input',
                                },
                                {
                                    label: t('information.form.measurementRegistrationKind.options.attachment'),
                                    value: 'Attachment',
                                },
                                {
                                    label: t('information.form.measurementRegistrationKind.options.request'),
                                    value: 'Request',
                                },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </inspectionInformationForm.Field>
            <inspectionInformationForm.Subscribe selector={({ values }) => values.distributionBoardMeasurementRegistrationKind}>
                {registrationKind => registrationKind !== 'Input' && registrationKind !== null && (
                    <FileField />
                )}
            </inspectionInformationForm.Subscribe>

            <Separator orientation="horizontal" />

            <div className="flex flex-col gap-4">
                <Label
                    label={t('information.form.measurementInstruments.label')}
                    className="w-full"
                >
                    <SingleOptionField
                        handleChange={async (e) => {
                            // Get the device data before the optimistic update removes it from cache
                            const deviceData = selectedMeasurementDevices?.data?.Devices?.find(item => item.ElectricalMeasurementDeviceId === e);

                            selectMeasurementDeviceCreate.mutate({
                                body: {
                                    ElectricalMeasurementDeviceId: e,
                                    ElectricalProjectVersionId: params.projectVersionId,
                                    InspectionKinds: deviceData?.InspectionKinds || [],
                                },
                            });
                        }}
                        state={{ value: '' }}
                        options={measurementDevicesOptions}
                        render={SingleCheckOptionItem}
                        dropdown
                        disabled={(selectMeasurementDeviceCreate.isPending && isOnline) || measurementDevicesOptions.length === 0}
                        emptyOptionsMessage={t('information.form.measurementInstruments.emptyMessage')}
                        placeholder={measurementDevicesOptions.length === 0 ? t('information.form.measurementInstruments.emptyMessage') : t('information.form.measurementInstruments.placeholder')}
                        closeOnClick
                    />
                </Label>

                <MeasurementDeviceSelectionTable
                    devices={measurementDevicesData || []}
                    inspectionKinds={inspectionKindsArray}
                    projectVersionId={params.projectVersionId}
                    onRemove={(deviceId) => {
                        selectMeasurementDeviceRemove.mutate({
                            params: {
                                query: {
                                    ElectricalProjectMeasurementDeviceId: deviceId,
                                },
                            },
                        });
                    }}
                />
            </div>

        </div>
    );
}

function Footer() {
    const { t } = useTranslation('common');

    const params = Route.useParams();
    const search = Route.useSearch();

    return (
        <footer className="flex justify-between gap-4">
            <Button variant="ghost" asChild>
                <Link
                    to="/projects/$projectVersionId/schedules"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.previous')}
                </Link>
            </Button>
            <Button asChild variant="ghost">
                <Link
                    to="/projects/$projectVersionId/re-inspections/distributionboards"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.next')}
                </Link>
            </Button>
        </footer>
    );
}

function FileField() {
    const params = Route.useParams();
    const isOnline = useOnline();

    const { files } = useElectricalProjectFiles();

    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/Measurements',
        mutationOptions: {
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    function handleChange(fileList: FileList) {
        const file = fileList[0];

        if (!file)
            return;

        fileUpload.upload({
            file,
            query: {
                ElectricalProjectFileId: crypto.randomUUID(),
            },
        });
    }

    const measurementFiles = useMemo(() => {
        return files?.filter(file => file.Kind === 'Measurements');
    }, [files]);

    return (
        <>
            <FileInput
                type="image"
                accept={fileUpload.accept ?? undefined}
                onChange={handleChange}
                disabled={isOnline && fileUpload.isDisabled}
                mode="both"
            />
            <div className="flex flex-col gap-2">
                {measurementFiles?.map(file => (
                    <div
                        key={file.ElectricalProjectFileId!}
                        className="relative flex flex-row gap-2"
                    >
                        <ElectricalProjectFilePreview
                            electricalProjectFileId={file.ElectricalProjectFileId!}
                        />
                        <Button
                            className="absolute top-1 right-1 bg-background/50"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                                fileDelete.delete(file.ElectricalProjectFileId!);
                            }}
                            disabled={fileDelete.isDisabled && !isOnline}
                        >
                            {fileDelete?.isDisabled && !isOnline ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </Button>
                    </div>
                ))}
            </div>
        </>
    );
}

/*
type SwitchingAuthorityType = 'distributionBox' | 'excluded';

type SwitchingAuthority =
    | {
        type: SwitchingAuthorityType;
        name: string;
        timeOut: string;
        timeIn: string;
    }
    | {
        type: SwitchingAuthorityType;
        name: string;
        subject: string;
        reason: string;
    };
function SwitchingAuthorityContent() {
    const { t } = useTranslation('re-inspections');

    const [switchingAuthorityType, setSwitchingAuthorityType] =
        useState<SwitchingAuthorityType>('distributionBox');

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
                <h3 className="text-lg font-semibold">
                    {t('information.modal.switchingAuthority.label')}
                </h3>
                <Modal />
            </div>
            <div className="flex flex-col gap-4 md:grid-cols-2">
                <SingleOptionField
                    name="type"
                    state={{ value: switchingAuthorityType }}
                    handleChange={(value) => setSwitchingAuthorityType(value)}
                    className="flex flex-row items-center justify-between"
                    options={[
                        {
                            label: t(
                                'information.modal.type.distributionBox.label'
                            ),
                            value: 'distributionBox',
                        },
                        {
                            label: t('information.modal.type.excluded.label'),
                            value: 'excluded',
                        },
                    ]}
                    render={SingleHorizontalOptionItemWithoutDot}
                />
                {contents
                    .filter(
                        (content) => content.type === switchingAuthorityType
                    )
                    .map((content, index) => (
                        <div key={index} className="flex gap-4 bg-white p-2">
                            <div className="flex w-full items-center justify-between gap-2">
                                {switchingAuthorityType ===
                                    'distributionBox' ? (
                                    <>
                                        <div className="flex items-center gap-2">
                                            <Clock />
                                            <p>
                                                {content.timeOut} -{' '}
                                                {content.timeIn}
                                            </p>
                                            <p>{content.name}</p>
                                        </div>
                                        <Button variant="ghost">
                                            <EllipsisVertical />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-2">
                                            <p>
                                                {content.name}, {content.reason}
                                            </p>
                                        </div>
                                        <Button variant="ghost">
                                            <EllipsisVertical />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
}

function SwitchingAuthorityForm() {
    const { t } = useTranslation('re-inspections');

    const { Field, handleSubmit, Subscribe } = useForm<SwitchingAuthority>({
        defaultValues: {
            type: 'distributionBox',
            name: '',
            timeOut: '',
            timeIn: '',
            reason: '',
            subject: '',
        },
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4 py-6">
            <Field name="type">
                {({ handleChange, state, name }) => (
                    <SingleOptionField
                        name={name}
                        handleChange={handleChange}
                        state={state}
                        className="flex flex-row items-center justify-between"
                        options={[
                            {
                                label: t(
                                    'information.modal.type.distributionBox.label'
                                ),
                                value: 'distributionBox',
                            },
                            {
                                label: t(
                                    'information.modal.type.excluded.label'
                                ),
                                value: 'excluded',
                            },
                        ]}
                        render={SingleHorizontalOptionItem}
                    />
                )}
            </Field>

            <Field name="name">
                {({ handleChange, state, name }) => (
                    <Label label={t('information.modal.name.label')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={(e) => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'information.modal.name.placeholder'
                            )}
                        />
                    </Label>
                )}
            </Field>

            <Subscribe selector={(state) => [state.values.type]}>
                {([type]) =>
                    type === 'distributionBox' ? (
                        <>
                            <Field name="timeOut">
                                {({ handleChange, state }) => (
                                    <Label
                                        label={t(
                                            'information.modal.timeOut.label'
                                        )}
                                    >
                                        <TimeField
                                            onChange={handleChange}
                                            value={state.value}
                                        />
                                    </Label>
                                )}
                            </Field>
                            <Field name="timeIn">
                                {({ handleChange, state }) => (
                                    <Label
                                        label={t(
                                            'information.modal.timeIn.label'
                                        )}
                                    >
                                        <TimeField
                                            onChange={handleChange}
                                            value={state.value}
                                        />
                                    </Label>
                                )}
                            </Field>
                        </>
                    ) : (
                        <>
                            <Field name="subject">
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'information.modal.subject.label'
                                        )}
                                    >
                                        <Input
                                            type="text"
                                            name={name}
                                            onChange={(e) =>
                                                handleChange(e.target.value)
                                            }
                                            value={state.value}
                                        />
                                    </Label>
                                )}
                            </Field>
                            <Field name="reason">
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'information.modal.reason.label'
                                        )}
                                    >
                                        <Textarea
                                            type="text"
                                            name={name}
                                            onChange={(e) =>
                                                handleChange(e.target.value)
                                            }
                                            value={state.value}
                                        />
                                    </Label>
                                )}
                            </Field>
                        </>
                    )
                }
            </Subscribe>
        </form>
    );
}

function Modal() {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    return (
        <Popout>
            <PopoutTrigger asChild>
                <Button variant="outline">{c('actions.general.add')}</Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader className="flex-row items-center justify-between">
                    <PopoutTitle>
                        {t('information.modal.addSwitchingAuthority.label')}
                    </PopoutTitle>
                    <PopoutClose asChild>
                        <Button variant="ghost" size="icon">
                            <X />
                        </Button>
                    </PopoutClose>
                </PopoutHeader>
                <PopoutBody>
                    <SwitchingAuthorityForm />
                </PopoutBody>
                <PopoutFooter>
                    <PopoutClose asChild>
                        <Button variant="ghost">Cancel</Button>
                    </PopoutClose>
                    <PopoutClose asChild>
                        <Button>Save</Button>
                    </PopoutClose>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
} */
